import { __awaiter, __generator, __read } from "tslib";
import { mergeStyleSets } from '@fluentui/react';
import { Body1, Button, Caption1, Card, CardFooter, CardHeader, CardPreview, Menu, MenuItem, MenuList, MenuPopover, MenuTrigger, mergeClasses, } from '@fluentui/react-components';
import { CalendarLtr32Regular, ChevronDown16Regular, ChevronUp16Regular, } from '@fluentui/react-icons';
import { ActionsMenuButton, RightClickActionsMenu, useActionsMenuRenderer, } from '@office-start/document-control';
import DocumentIconControl from '@office-start/document-icon-control';
import { getDocumentThumbnailUrl } from '@office-start/document-service';
import { getFileTypeNameFromType } from '@office-start/file-utility';
import { format } from '@office-start/string-formatter-utility';
import React, { lazy, Suspense, useEffect, useMemo, useRef, useState, } from 'react';
import { useCreateDocumentActions } from '../hooks/useCreateDocumentActions';
import { useCardStyles } from './EdgeworthItemCardControl.styles';
import { CardPreviewArtifactHeight, CardPreviewHeight } from './StyleConstants';
var LazyTagGroupMenuControl = lazy(function () { return import('@office-start/tag-group-menu-control'); });
export var EdgeworthItemCardControl = function (_a) {
    var createDocumentActionsOptions = _a.createDocumentActionsOptions, edgeworthItemResources = _a.edgeworthItemResources, edgeworthTaggingResources = _a.edgeworthTaggingResources, itemElementId = _a.itemElementId, onActionMenuToggled = _a.onActionMenuToggled, onMeetingArtifactClick = _a.onMeetingArtifactClick, onDocClick = _a.onDocClick, item = _a.item, index = _a.index, thumbnailPreviewBackgroundColor = _a.thumbnailPreviewBackgroundColor, tagItems = _a.tagItems, onTagItemClick = _a.onTagItemClick, descriptionTextOverride = _a.descriptionTextOverride;
    var cardClasses = useCardStyles();
    var componentRef = useRef(null);
    var actionsMenuRenderingPhase = useActionsMenuRenderer(componentRef);
    var _b = useCreateDocumentActions(actionsMenuRenderingPhase, item, createDocumentActionsOptions), actionMenuItems = _b.actionMenuItems, contentActionsEnabled = _b.contentActionsEnabled, contentActionsGroupInfo = _b.contentActionsGroupInfo;
    var _c = __read(useState(''), 2), thumbnailUrl = _c[0], setThumbnailUrl = _c[1];
    var _d = __read(useState(false), 2), thumbnailErrored = _d[0], setThumbnailErrored = _d[1];
    var handleThumbnailError = function () {
        setThumbnailErrored(true);
    };
    var _e = __read(React.useState(false), 2), open = _e[0], setOpen = _e[1];
    var onOpenChange = function (e, data) {
        e.preventDefault();
        setOpen(data.open);
        e.stopPropagation();
    };
    var meetingArtifacts = useMemo(function () {
        return item.meetingObjects || [];
    }, [item]);
    var showMeetingArtifacts = useMemo(function () {
        return meetingArtifacts && meetingArtifacts.length > 0;
    }, [meetingArtifacts]);
    var previewHeight = useMemo(function () {
        return showMeetingArtifacts
            ? CardPreviewArtifactHeight
            : CardPreviewHeight;
    }, [showMeetingArtifacts]);
    var descriptionText = useMemo(function () {
        var _a, _b, _c;
        if (descriptionTextOverride) {
            return descriptionTextOverride;
        }
        return showMeetingArtifacts
            ? format((_a = edgeworthItemResources.meetingArtifactCountTitle) !== null && _a !== void 0 ? _a : '', (_b = meetingArtifacts.length.toString()) !== null && _b !== void 0 ? _b : '')
            : (_c = item.activity) === null || _c === void 0 ? void 0 : _c.plainText;
    }, [
        descriptionTextOverride,
        showMeetingArtifacts,
        meetingArtifacts,
        item,
        edgeworthItemResources.meetingArtifactCountTitle,
    ]);
    var cardPreview = useMemo(function () {
        return (React.createElement(CardPreview, { style: { height: previewHeight } }, thumbnailUrl && !thumbnailErrored ? (React.createElement("img", { className: mergeClasses(cardClasses.previewThumbnail, showMeetingArtifacts &&
                cardClasses.meetingObjectPreviewThumbnail), src: thumbnailUrl, alt: item.title, onError: handleThumbnailError })) : (React.createElement("div", { className: mergeClasses(cardClasses.previewErrorThumbnail, showMeetingArtifacts &&
                cardClasses.meetingObjectPreviewThumbnail), style: mergeStyleSets({ display: 'flex' }, {
                backgroundColor: thumbnailPreviewBackgroundColor
                    ? thumbnailPreviewBackgroundColor +
                        '25'
                    : '',
            }) }, !showMeetingArtifacts ? (React.createElement(DocumentIconControl, { item: item, iconSize: "64x64", customIconAltText: edgeworthItemResources.customIconAltText })) : (React.createElement("div", { className: cardClasses.meetingObjectPreviewThumbnailIcon },
            React.createElement(CalendarLtr32Regular, null)))))));
    }, [
        cardClasses.meetingObjectPreviewThumbnail,
        cardClasses.meetingObjectPreviewThumbnailIcon,
        cardClasses.previewErrorThumbnail,
        cardClasses.previewThumbnail,
        item,
        edgeworthItemResources.customIconAltText,
        previewHeight,
        showMeetingArtifacts,
        thumbnailErrored,
        thumbnailPreviewBackgroundColor,
        thumbnailUrl,
    ]);
    var artifactsMenuItems = useMemo(function () {
        return (meetingArtifacts &&
            meetingArtifacts.map(function (artifact) {
                return (React.createElement(MenuItem, { style: {
                        lineHeight: '16px',
                    }, key: artifact.id, icon: React.createElement(DocumentIconControl, { item: artifact, iconSize: "16x16" }), onClick: function (event) {
                        event.preventDefault();
                        onMeetingArtifactClick &&
                            onMeetingArtifactClick(artifact);
                        event.stopPropagation();
                    } },
                    React.createElement(Caption1, { block: true, truncate: true, wrap: false, style: {
                            height: '20px',
                        }, title: artifact.title }, artifact.title)));
            }));
    }, [meetingArtifacts, onMeetingArtifactClick]);
    var gridViewCardAriaLabel = useMemo(function () {
        return format(edgeworthItemResources.gridViewCardItemAriaLabel, getFileTypeNameFromType(item.fileType), item.title);
    }, [
        edgeworthItemResources.gridViewCardItemAriaLabel,
        item.fileType,
        item.title,
    ]);
    useEffect(function () {
        var isMounted = true;
        var fetchThumbnail = function () { return __awaiter(void 0, void 0, void 0, function () {
            var url;
            return __generator(this, function (_a) {
                switch (_a.label) {
                    case 0:
                        if (!getDocumentThumbnailUrl) return [3 /*break*/, 2];
                        return [4 /*yield*/, getDocumentThumbnailUrl(item)];
                    case 1:
                        url = _a.sent();
                        if (isMounted && url) {
                            setThumbnailUrl(url);
                        }
                        _a.label = 2;
                    case 2: return [2 /*return*/];
                }
            });
        }); };
        fetchThumbnail();
        return function () {
            isMounted = false;
        };
    }, [item]);
    return (React.createElement("div", { ref: componentRef, className: cardClasses.calloutContainer },
        React.createElement(Card, { className: cardClasses.root, key: item.id, id: itemElementId, onClick: onDocClick, tabIndex: 0, "aria-label": gridViewCardAriaLabel },
            cardPreview,
            React.createElement(CardHeader, { className: cardClasses.headerRoot, image: React.createElement(DocumentIconControl, { item: item, iconSize: "32x32" }), header: React.createElement("div", { title: item.title, className: cardClasses.headerTitle, tabIndex: 0 },
                    React.createElement(Body1, { block: true, wrap: false, className: cardClasses.headerTitleBody },
                        React.createElement("span", { className: cardClasses.headerTitleText }, item.title),
                        tagItems ? (React.createElement("div", { className: cardClasses.tagGroup },
                            React.createElement(Suspense, { fallback: null },
                                React.createElement(LazyTagGroupMenuControl, { tagItems: tagItems, onTagItemClick: onTagItemClick, tagGroupAriaLabel: format(edgeworthTaggingResources.tagGroupAriaLabel, tagItems.length.toString()) })))) : null)), description: React.createElement("div", { className: cardClasses.headerSubTitle, tabIndex: 0 },
                    React.createElement(Caption1, { block: true, truncate: true, wrap: false, title: descriptionText }, descriptionText)), action: actionMenuItems.length > 0 &&
                    actionsMenuRenderingPhase ? (React.createElement(ActionsMenuButton, { actions: actionMenuItems, ariaLabel: edgeworthItemResources.moreActionsButtonAriaLabel, buttonClasses: [
                        cardClasses.documentListItemIconMenu,
                        cardClasses.actionsMenuLink,
                        'show',
                    ], contentActionsEnabled: contentActionsEnabled, groupInfo: contentActionsGroupInfo, item: item, onActionMenuToggled: onActionMenuToggled })) : null }),
            showMeetingArtifacts ? (React.createElement(CardFooter, null,
                React.createElement(Menu, { open: open, onOpenChange: onOpenChange, key: index },
                    React.createElement(MenuTrigger, null,
                        React.createElement(Button, { className: cardClasses.artifactButton, icon: !open ? (React.createElement(ChevronDown16Regular, null)) : (React.createElement(ChevronUp16Regular, null)) })),
                    React.createElement(MenuPopover, { className: cardClasses.artifactMenuPopOver },
                        React.createElement(MenuList, null, artifactsMenuItems))))) : null),
        actionMenuItems.length > 0 && actionsMenuRenderingPhase ? (React.createElement(RightClickActionsMenu, { actions: actionMenuItems, componentRef: componentRef, contentActionsEnabled: contentActionsEnabled, groupInfo: contentActionsGroupInfo, item: item, onActionMenuToggled: onActionMenuToggled })) : null));
};
//# sourceMappingURL=EdgeworthItemCardControl.js.map